import { useState, useEffect } from 'react'
import { AlertCircle, Info, AlertTriangle, CheckCircle, X } from 'lucide-react'
import { Button } from './button'

export function AlertDialog({
  open,
  onOpenChange,
  title,
  description,
  details = [], // Array of detail messages
  variant = 'info', // 'info', 'warning', 'error', 'success'
  confirmText = 'OK',
  onClose
}) {
  const [isClosing, setIsClosing] = useState(false)

  // Reset closing state when dialog opens
  useEffect(() => {
    if (open) {
      setIsClosing(false)
    }
  }, [open])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && open) {
        handleClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open])

  // Handle Enter key
  useEffect(() => {
    const handleEnter = (e) => {
      if (e.key === 'Enter' && open) {
        handleClose()
      }
    }

    document.addEventListener('keydown', handleEnter)
    return () => document.removeEventListener('keydown', handleEnter)
  }, [open])

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      onClose?.()
      onOpenChange?.(false)
    }, 200)
  }

  if (!open) return null

  const icons = {
    info: <Info className="h-6 w-6 text-info" />,
    warning: <AlertTriangle className="h-6 w-6 text-warning" />,
    error: <AlertCircle className="h-6 w-6 text-destructive" />,
    success: <CheckCircle className="h-6 w-6 text-success" />
  }

  const bgColors = {
    info: 'bg-info/10',
    warning: 'bg-warning/10',
    error: 'bg-destructive/10',
    success: 'bg-success/10'
  }

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-200 ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
        onClick={handleClose}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className={`bg-card border rounded-lg shadow-xl max-w-md w-full pointer-events-auto transition-all duration-300 ${
            isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 pb-4">
            <div className="flex items-start gap-3">
              <div className={`flex-shrink-0 w-12 h-12 rounded-full ${bgColors[variant]} flex items-center justify-center`}>
                {icons[variant]}
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">{title}</h2>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            <p className="text-muted-foreground mb-4">{description}</p>

            {details.length > 0 && (
              <ul className="space-y-2 bg-muted/30 rounded-lg p-4 border">
                {details.map((detail, index) => (
                  <li key={index} className="text-sm text-foreground flex items-start gap-2">
                    <span className="text-muted-foreground mt-0.5">•</span>
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-muted/30 border-t">
            <Button onClick={handleClose} autoFocus>
              {confirmText}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
